import context from '@truckdown/components-systems';
import { ApiResultModel } from '@truckdown/systems';
import { IUserCreator, RegisterUserModel, UserRegistrationType, UserInviteModel } from '@truckdown/accounts';
import { ValidationResultModel, AddressStatus, LoginWithValidationTokenModel, LoginWithPasswordModel, HttpMethod } from '@truckdown/systems';
import { defineComponent, ref, computed, Ref, watch, nextTick, ComputedRef } from 'vue';

const preventDoubleClicks = function (data: IData) {
    return {
        runAsync: async function (f: () => Promise<void>) {
            if (!data.isInProgress.value) {
                data.isInProgress.value = true;
                try {
                    await f();
                } finally {
                    data.isInProgress.value = false;
                }
            }
        }
    }
}

interface IData {
    isInProgress: Ref<boolean>,
    loginErrorMessages: Ref<string[]>,
    stage: Ref<string>,
    //username
    username: Ref<string>,
    usernameLabel: Ref<string>,
    infoMessage: Ref<string>,
    usernameType: Ref<'email' | 'sms' | 'username' | ''>,
    usernameToken: Ref<string>,
    isUsernameValidated: Ref<boolean>,
    usernameResetTrigger: Ref<number>,
    //alternate username
    requireAlternate: Ref<boolean>,
    alternateUsername: Ref<string>,
    alternateUsernameToken: Ref<string>,
    alternateUsernameResetTrigger: Ref<number>,
    isAlternateUsernameValidated: Ref<boolean>,
    //login type
    passwordctrl: Ref<HTMLInputElement | null>,
    usePassword: Ref<boolean>,
    password: Ref<string>,
    //register
    firstName: Ref<string>,
    lastName: Ref<string>,
    companyName: Ref<string>,
    phone: Ref<string>,
    newPassword: Ref<string>,
    confirmPassword: Ref<string>,
    marketing: Ref<boolean>,
    terms: Ref<boolean>,
    selectedAccountType: Ref<string>,
    registerValidations: Ref<Map<string, string>>,
    userInvite: Ref<UserInviteModel | undefined>,
    returnUrl: Ref<string>
}

const getData = function (props: any) {
    return {
        isInProgress: ref<boolean>(false),
        loginErrorMessages: ref<string[]>([]),
        stage: ref<string>(props.userInvite != null ? 'invite' : props.stage ?? 'login'),
        //username
        username: ref<string>(props.userInvite?.username ?? props.address ?? ''),
        usernameLabel: ref<string>(props.userInvite != null ? 'Email' : 'Email or Mobile Number'),
        infoMessage: ref<string>(""),
        usernameType: ref<'email' | 'sms' | 'username' | ''>('email'),
        usernameToken: ref<string>(''),
        isUsernameValidated: ref<boolean>(false),
        usernameResetTrigger: ref<number>(0),
        //alternate username
        requireAlternate: ref<boolean>(true),
        alternateUsername: ref<string>(''),
        alternateUsernameToken: ref<string>(''),
        alternateUsernameResetTrigger: ref<number>(0),
        isAlternateUsernameValidated: ref<boolean>(false),
        //login type
        passwordctrl: ref<HTMLInputElement | null>(null),
        usePassword: ref<boolean>(false),
        password: ref<string>(''),
        //register
        firstName: ref<string>(props.userInvite?.firstName ?? ''),
        lastName: ref<string>(props.userInvite?.lastName ?? ''),
        companyName: ref<string>(props.userInvite?.accountName ?? ''),
        phone: ref<string>(''),
        newPassword: ref<string>(''),
        confirmPassword: ref<string>(''),
        marketing: ref<boolean>(true),
        terms: ref<boolean>(false),
        selectedAccountType: ref<string>('Select Account Type'),
        registerValidations: ref<Map<string, string>>(new Map()),
        userInvite: ref<UserInviteModel | undefined>(props.userInvite),
        returnUrl: ref<string>(props.returnUrl ?? '')
    } as IData;
}

interface IComputed {
    alternateUsernameType: ComputedRef<'sms' | 'email'>,
    alternatePlaceholder: ComputedRef<string>,
    isLoggingInWithPassword: ComputedRef<boolean>
}

const getComputed = function (data: IData) {
    return {
        alternateUsernameType: computed(() => {
            if (data.usernameType.value == 'email') {
                return 'sms';
            }
            return 'email';
        }),
        alternatePlaceholder: computed(() => {
            if (data.usernameType.value == 'email') {
                data.usernameLabel.value = "Email";
                data.infoMessage.value = "The email you entered does not exist in our system. Please continue to register a new account."
                return 'Mobile Number (Optional)';

            } else {
                data.usernameLabel.value = "Mobile Number";
                data.infoMessage.value = "The mobile number you entered does not exist in our system. Please continue to register a new account."
                return 'Email';
            }

        }),
        hasMobile: computed(() => {
            return data.usernameType.value == 'sms' || (data.alternateUsername.value?.length ?? 0) > 0;
        }),
        isLoggingInWithPassword: computed(() => {
            return data.usernameType.value == 'username'
                || data.usePassword.value == true;
        })
    } as IComputed;
}

const getMethods = function (data: IData, computed: IComputed, props: any) {
    const inProgress = preventDoubleClicks(data);
    const phoneRegex = /^1?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;

    const isValidPhoneNumber = function (number: string) {
        if (!number || number.length == 0) {
            return true;
        }
        const match = number.match(phoneRegex);
        if ((match?.length ?? 0) > 0) {
            return true;
        }
        return false;
    };

    const sendLoginUsingTokenRequest = function (address: string, token: string) {
        //NOTE: runs a request to local app to ensure login state is set

        const manager = context.apiManager;
        let model: LoginWithValidationTokenModel = {
            username: address,
            validationToken: token
        };

        let req = manager.createRequest({
            service: 'main',
            path: '/accounts/login-with-token',
            method: HttpMethod.POST,
            includeAntiforgery: true
        }, model);

        return manager.get<ApiResultModel>(req);
    }

    const loginUsingToken = function (address: string, token: string) {
        return inProgress.runAsync(async () => {
            data.loginErrorMessages.value = [];

            let res = await sendLoginUsingTokenRequest(address, token);
            if (res.ok) {
                loginSuccessfulRedirect();
            } else {
                if (data.username.value == address) {
                    data.usernameResetTrigger.value++;
                }
                if (data.alternateUsername.value == address) {
                    data.alternateUsernameResetTrigger.value++;
                }
                data.loginErrorMessages.value.push(res.message ?? '');
            }
        });
    };

    const loginUsingPassword = function () {
        const manager = context.apiManager;
        let model: LoginWithPasswordModel = {
            un: data.username.value,
            pass: data.password.value
        };

        let req = manager.createRequest({
            service: 'main',
            path: '/accounts/login',
            method: HttpMethod.POST,
            includeAntiforgery: true
        }, model);

        return manager.get<ApiResultModel>(req);
    }

    const authenticateNewUser = function (model: RegisterUserModel) {
        return sendLoginUsingTokenRequest(model.email, model.emailToken)
            .then((res) => {
                if (res.ok) {
                    if (model.type == UserRegistrationType.Vendor) {
                        window.location.href = '/accounts/welcome/vendor';
                    } else if (model.type == UserRegistrationType.Fleet) {
                        window.location.href = '/accounts/welcome/fleet';
                    } else {
                        window.location.href = '/accounts/welcome/individual';
                    }
                } else {
                    data.registerValidations.value.set('', 'Your account was created but there was an error logging you in');
                }
            })
            .catch((reason) => {
                console.error('Error logging in', reason);
                data.registerValidations.value.set('', 'Your account was created but there was an error logging you in');
            });
    };

    const registerUser = function (type: string, email: string, emailToken: string, mobile: string, mobileToken: string) {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return context.getService<IUserCreator>('IUserCreator')
            .then((userCreator) => {
                let model = {
                    type: type == 'fleet' ? UserRegistrationType.Fleet
                        : (type == 'vendor' ? UserRegistrationType.Vendor
                            : UserRegistrationType.Free),
                    timezone: timezone,
                    email: email,
                    emailToken: emailToken,
                    firstName: data.firstName.value,
                    lastName: data.lastName.value,
                    companyName: data.companyName.value,
                    phone: data.phone.value,
                    password: data.newPassword.value,
                    acceptTerms: data.terms.value,
                    allowMarketing: data.marketing.value,
                } as RegisterUserModel;

                if (mobile.length > 0) {
                    model.mobile = mobile;
                    model.mobileToken = mobileToken;
                }

                if (data.stage.value == 'invite') {
                    model.inviteId = data.userInvite.value?.id ?? undefined;
                    model.email = data.username.value;
                    model.emailToken = "invite";
                    model.mobile = undefined;
                }

                return userCreator.registerNewUser(model)
                    .then(async (res) => {
                        if (res.ok) {
                            var label = type == 'fleet' ? 'Fleet'
                                : (type == 'vendor' ? 'Vendor'
                                    : 'User');

                            context.analytics({
                                'event': 'Register',
                                'event_category': 'Security',
                                'event_label': label
                            });

                            if (data.userInvite != null && data.stage.value == 'invite') {
                                data.password.value = data.newPassword.value;
                                var loginRes = await loginUsingPassword();
                                if (loginRes.ok) {
                                    inviteRedirect(model.type);
                                } else {
                                    data.loginErrorMessages.value.push(loginRes.message ?? '');
                                }
                            } else {
                                await authenticateNewUser(model);
                            }
                        } else {
                            data.registerValidations.value.set('', res.message ?? '');
                        }
                    });
            });
    };

    const validateForRegistration = async function () {
        if (data.firstName.value.length == 0) {
            data.registerValidations.value.set('firstName', 'Please provide your first name.');
            await nextTick();
            window.scrollTo({ top: 100, behavior: "smooth" });
        }

        if (data.lastName.value.length == 0) {
            data.registerValidations.value.set('lastName', 'Please provide your last name.');
            await nextTick();
            window.scrollTo({ top: 100, behavior: "smooth" });
        }

        if (data.stage.value != 'invite') {
            if (data.phone.value.length == 0) {
                data.registerValidations.value.set('phone', 'A contact phone number is required.');
                await nextTick();
                window.scrollTo({ top: 200, behavior: "smooth" });
            }

            if (data.phone.value.length > 0 && !isValidPhoneNumber(data.phone.value)) {
                data.registerValidations.value.set('phone', 'Phone number is not the correct format.');
                await nextTick();
                window.scrollTo({ top: 200, behavior: "smooth" });
            }
        }

        if (data.newPassword.value.length == 0) {
            data.registerValidations.value.set('newPassword', 'Please provide a password for accessing your account.');
            if (data.stage.value != 'invite') {
                await nextTick();
                window.scrollTo({ top: 300, behavior: "smooth" });
            }
        }
        if (data.newPassword.value.length < 6) {
            data.registerValidations.value.set('newPassword', 'Password must be at least 6 digits long.');
            if (data.stage.value != 'invite') {
                await nextTick();
                window.scrollTo({ top: 300, behavior: "smooth" });
            }
        }
        if (data.confirmPassword.value != data.newPassword.value) {
            data.registerValidations.value.set('confirmPassword', 'The passwords do not match.');
            await nextTick();
            window.scrollTo({ top: 400, behavior: "smooth" });
        }
        if (!data.terms.value) {
            data.registerValidations.value.set('terms', 'You must accept our terms & conditions to continue.');
            if (data.stage.value != 'invite') {
                await nextTick();
                window.scrollTo({ top: 400, behavior: "smooth" });
            }
        }

        if (data.stage.value != 'invite') {
            if (!data.selectedAccountType.value || data.selectedAccountType.value === 'Select Account Type') {
                data.registerValidations.value.set('selectedAccountType', 'You must select account type.');
            }
        }
    };

    const inviteRedirect = function (type: UserRegistrationType) {
        window.location.href = '/accounts/welcome/invited';
    };

    const loginSuccessfulRedirect = function () {
        if (data.returnUrl.value && data.returnUrl.value.length > 0) {
            if (data.returnUrl.value[0] == "/") {
                window.location.href = data.returnUrl.value;
            } else {
                window.location.href = '/';
            }
        } else {
            window.location.href = '/';
        }
    };

    return {
        skipAlternate: function () {
            data.stage.value = 'register';
        },
        validateUsername: function (type: 'email' | 'sms' | 'username', result: ValidationResultModel) {
            const methods = this;

            data.usernameType.value = type;
            data.usernameToken.value = result?.validationToken ?? '';
            data.isUsernameValidated.value = true;

            if (type == 'username') {
                data.usePassword.value = true;
                nextTick(() => {
                    if (data.passwordctrl.value) {
                        data.passwordctrl.value.focus();
                    }
                });
            } else {
                if (data.usernameType.value == 'email') {
                    data.requireAlternate.value = false;
                }

                if (result) {
                    if (result.status == AddressStatus.Inactive) {
                        data.stage.value = 'inactive';
                    } else if (result.status == AddressStatus.Active) {
                        return loginUsingToken(result.address, result.validationToken)
                            .catch((reason) => {
                                console.error(reason);
                            });
                    } else {
                        data.stage.value = 'register-start';
                    }
                }
            }

            return Promise.resolve();
        },
        validateAlternate: function (type: 'email' | 'sms' | 'username', result: ValidationResultModel) {
            data.alternateUsernameToken.value = result?.validationToken ?? '';
            data.isAlternateUsernameValidated.value = true;

            if (result.status == AddressStatus.Inactive) {
                data.stage.value = 'inactive';
            } else if (result.status == AddressStatus.Active) {
                return loginUsingToken(result.address, result.validationToken)
                    .catch((reason) => {
                        console.error(reason);
                    });
            } else {
                data.stage.value = 'register';
            }
            return Promise.resolve();
        },
        showLoginWithCode: function (ev: Event) {
            if (data.usernameType.value == 'username') {
                data.usernameType.value = '';
            }
            data.usePassword.value = false;
            data.loginErrorMessages.value = [];
        },
        showLoginWithPassword: function (ev: Event) {
            data.usePassword.value = true;
            nextTick(() => {
                if (data.username.value.length > 0 && data.passwordctrl.value) {
                    data.passwordctrl.value.focus();
                }
            });
        },
        loginUsingToken: loginUsingToken,
        loginWithPassword: function (ev: Event) {
            return inProgress.runAsync(async () => {
                //login against accounts app
                data.loginErrorMessages.value = [];

                if (data.username.value.length == 0) {
                    data.loginErrorMessages.value.push('Please provide your username.');
                }

                if (data.password.value.length == 0) {
                    data.loginErrorMessages.value.push('Please enter your password.');
                }

                var res = await loginUsingPassword();
                if (res.ok) {
                    loginSuccessfulRedirect();
                } else {
                    data.loginErrorMessages.value.push(res.message ?? '');
                }
            });
        },
        hasValidationError: function (field: string) {
            return data.registerValidations.value.has(field);
        },
        getValidationError: function (field: string) {
            return data.registerValidations.value.get(field);

        },
        isValidPhoneNumber: isValidPhoneNumber,
        validateForRegistration: validateForRegistration,
        registerUser: registerUser,
        authenticateNewUser: authenticateNewUser,
        createAccount: function (type: 'vendor' | 'free' | 'fleet') {
            return inProgress.runAsync(async () => {
                data.registerValidations.value.clear();

                //email
                var email = data.username.value;
                var emailToken = data.usernameToken.value;

                var mobile = '';
                var mobileToken = '';
                if (data.usernameType.value != 'email' && !data.userInvite.value) {
                    email = data.alternateUsername.value;
                    emailToken = data.alternateUsernameToken.value;
                    mobile = data.username.value;
                    mobileToken = data.usernameToken.value;

                    if (data.phone.value.length == 0) {
                        data.phone.value = data.username.value;
                    }
                }

                if (computed.alternateUsernameType.value == 'sms' && !data.userInvite.value) {
                    mobile = data.alternateUsername.value;
                    mobileToken = data.alternateUsernameToken.value;

                    if (data.phone.value.length == 0) {
                        data.phone.value = data.alternateUsername.value;
                    }
                }

                validateForRegistration();

                if (data.registerValidations.value.size == 0) {
                    await registerUser(type, email, emailToken, mobile, mobileToken);
                }
            });
        }
    }
}

export default defineComponent({
    props: {
        address: String,
        returnUrl: String,
        userInvite: {
            type: Object as () => UserInviteModel | null,
            required: false
        }
    },
    setup: function (props, ctx: any) {
        //loading
        const data = getData(props);

        const inProgress = preventDoubleClicks(data);
        const computed = getComputed(data);
        const methods = getMethods(data, computed, props);

        watch(data.username, function (newValue: string, oldValue: string) {
            if (data.stage.value != 'login') {
                data.stage.value = 'login';
            }
        });

        watch(data.alternateUsername, function (newValue: string, oldValue: string) {
            if (data.stage.value != 'register-start') {
                data.stage.value = 'register-start';
            }
        });

        return {
            ...data,
            ...computed,
            ...methods
        };
    }
});
